import { useState, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Eye, Upload, Sparkles, Flame, Users, Loader2, X } from "lucide-react";

export default function PageVisitorsPanel() {
  const [text, setText] = useState("");
  const [image, setImage] = useState(null); // data URL
  const [analyzing, setAnalyzing] = useState(false);
  const [companies, setCompanies] = useState(null); // [{name,count,...}]
  const [meta, setMeta] = useState({ total_rows: 0, unique: 0 });
  const fileRef = useRef(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please upload an image"); return; }
    const reader = new FileReader();
    reader.onload = () => setImage(reader.result);
    reader.readAsDataURL(f);
  };

  const analyze = async () => {
    if (!text.trim() && !image) { toast.error("Paste the visitor list and/or upload a screenshot"); return; }
    setAnalyzing(true);
    try {
      const { data } = await api.post("/apollo/visitor-companies", {
        text: text.trim() || undefined,
        image_base64: image || undefined,
      });
      setCompanies(data.companies || []);
      setMeta({ total_rows: data.total_rows || 0, unique: data.unique || 0 });
      if ((data.companies || []).length === 0) toast.info("No companies found — check the pasted text / screenshot");
      else toast.success(`Found ${data.unique} companies across ${data.total_rows} visits`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not analyze visitors");
    }
    setAnalyzing(false);
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="page-visitors-panel">
      <div className="lg:col-span-1 space-y-4">
        <div className="bg-signal-500/5 border border-signal-500/20 rounded-sm p-3 flex gap-3">
          <Sparkles className="w-4 h-4 text-signal-500 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-foreground/80 leading-relaxed">
            <strong className="text-signal-500">Turn page visits into targets.</strong> LinkedIn hides visitor
            names, but who keeps checking you out is strong intent. Paste your Company Page <em>Visitors</em>{" "}
            list (or drop a screenshot) — we rank the companies by visit frequency, then find real
            decision-makers there via Apollo.
          </div>
        </div>

        <div className="data-card space-y-3">
          <div className="label-eyebrow inline-flex items-center gap-2">
            <Eye className="w-3.5 h-3.5 text-signal-500" /> Visitor list
          </div>
          <Textarea
            data-testid="visitors-text-input"
            rows={8}
            placeholder={"Paste rows like:\nLogistics Specialist at Hunicorn Shipping\nSalesperson at SEDINA GROUP\nSomeone at ROSA CATENE SPA"}
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="text-xs font-mono"
          />
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="visitors-file-input" />
            <Button size="sm" variant="outline" className="rounded-sm" onClick={() => fileRef.current?.click()} data-testid="visitors-upload-btn">
              <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload screenshot
            </Button>
            {image && (
              <span className="text-[0.65rem] text-emerald-400 inline-flex items-center gap-1">
                image attached
                <button onClick={() => { setImage(null); if (fileRef.current) fileRef.current.value = ""; }} data-testid="visitors-clear-image"><X className="w-3 h-3" /></button>
              </span>
            )}
          </div>
          <Button onClick={analyze} disabled={analyzing} className="w-full rounded-sm" data-testid="visitors-analyze-btn">
            {analyzing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
            {analyzing ? "Analyzing…" : "Analyze visitors"}
          </Button>
        </div>
      </div>

      <div className="lg:col-span-2 space-y-3">
        {!companies && (
          <div className="data-card text-center py-16 text-muted-foreground">
            <Eye className="w-8 h-8 mx-auto mb-2" />
            <p className="text-sm">Paste or upload your Visitors list to see ranked target accounts.</p>
          </div>
        )}
        {companies && companies.length > 0 && (
          <>
            <div className="flex items-center justify-between">
              <span className="label-eyebrow">Ranked accounts · {meta.unique} companies / {meta.total_rows} visits</span>
              <span className="text-[0.65rem] text-muted-foreground">Highlighted = 2+ visits (hot)</span>
            </div>
            {companies.map((c) => (
              <CompanyRow key={c.name} company={c} />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function CompanyRow({ company }) {
  const hot = company.count >= 2;
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // {created, count}
  const pollRef = useRef(null);

  const findContacts = async () => {
    setBusy(true); setDone(null);
    try {
      const { data } = await api.post("/apollo/build-contacts", { companies: [company.name] });
      const jobId = data.job_id;
      // poll
      const poll = async () => {
        try {
          const { data: job } = await api.get(`/apollo/build-contacts/${jobId}`);
          if (job.status === "done" || job.status === "failed") {
            clearInterval(pollRef.current);
            setBusy(false);
            if (job.status === "failed") { toast.error(job.error || "Contact build failed"); return; }
            const created = job.result?.prospects_created ?? 0;
            setDone({ created });
            toast.success(`Built ${created} contact${created === 1 ? "" : "s"} at ${company.name}`, {
              description: created > 0 ? "Enroll them into a campaign from the Pipeline." : "No new contacts found for this company.",
            });
          }
        } catch {
          clearInterval(pollRef.current);
          setBusy(false);
        }
      };
      pollRef.current = setInterval(poll, 2500);
      poll();
    } catch (e) {
      setBusy(false);
      toast.error(e?.response?.data?.detail || "Could not start contact search");
    }
  };

  return (
    <div className={`data-card flex items-center justify-between gap-3 ${hot ? "border-amber-accent/50 bg-amber-accent/5" : ""}`} data-testid={`visitor-company-${company.name}`}>
      <div className="min-w-0 flex items-center gap-2.5">
        <span className={`text-xs font-semibold px-2 py-1 rounded-sm inline-flex items-center gap-1 shrink-0 ${hot ? "bg-amber-accent/15 text-amber-accent" : "bg-muted/40 text-muted-foreground"}`}>
          {hot && <Flame className="w-3 h-3" />}{company.count}×
        </span>
        <span className="text-sm font-medium truncate">{company.name}</span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {done && <span className="text-[0.65rem] text-emerald-400">+{done.created} prospects</span>}
        <Button size="sm" variant={hot ? "default" : "outline"} className="rounded-sm" onClick={findContacts} disabled={busy} data-testid={`visitor-find-${company.name}`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Users className="w-3.5 h-3.5 mr-1.5" />}
          {busy ? "Finding…" : "Find contacts"}
        </Button>
      </div>
    </div>
  );
}
